import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

function QrCodeField({ label, imageUrl, onUpload, uploading }) {
  const inputRef = React.useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) onUpload(f);
    e.target.value = '';
  };

  return (
    <div className="flex items-center justify-between p-3 border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
      <div className="flex items-center gap-3">
        <span className="text-body-sm" style={{ color: '#b8a68f' }}>{label}</span>
        {imageUrl ? (
          <img src={imageUrl} alt={label} className="w-16 h-16 object-contain rounded border" style={{ borderColor: '#3d5a4a', backgroundColor: '#fff' }} />
        ) : (
          <div className="w-16 h-16 flex items-center justify-center rounded border text-body-xs" style={{ borderColor: '#3d5a4a', color: '#999999' }}>
            None
          </div>
        )}
      </div>
      <div>
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} disabled={uploading} className="hidden" />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50"
          style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
        >
          {uploading ? 'Uploading...' : imageUrl ? 'Replace' : 'Upload'}
        </button>
      </div>
    </div>
  );
}

function SettingsField({ label, value, onSave, saving, type = 'text' }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const startEdit = () => {
    setEditValue(value ?? '');
    setEditing(true);
  };

  const save = async () => {
    await onSave(type === 'number' ? parseFloat(editValue) : editValue);
    setEditing(false);
  };

  return (
    <div className="flex items-center justify-between p-3 border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
      <span className="text-body-sm" style={{ color: '#b8a68f' }}>{label}</span>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            type={type}
            step={type === 'number' ? '0.01' : undefined}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            disabled={saving}
            className="px-2 py-1 text-sm rounded border"
            style={{ borderColor: '#3d5a4a' }}
          />
          <button onClick={save} disabled={saving} className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50" style={{ backgroundColor: '#c9a961', color: '#0d2818' }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={() => setEditing(false)} disabled={saving} className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50" style={{ backgroundColor: '#3d5a4a', color: '#b8a68f' }}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="font-medium" style={{ color: '#c9a961' }}>{value || '—'}</span>
          <button onClick={startEdit} className="px-3 py-1 text-xs rounded font-medium" style={{ backgroundColor: '#c9a961', color: '#0d2818' }}>
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminCreditTopups() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('pending_review');
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [processingId, setProcessingId] = useState(null);

  const [settings, setSettings] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [uploadingQr, setUploadingQr] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  const fetchTopups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/credit-topups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load top-ups');
      }
      setTopups(data.topups || []);
    } catch (err) {
      setError(err.message || 'Failed to load top-ups');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/credit-topup-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSettings(data.settings);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  }, [token]);

  useEffect(() => {
    if (!user || user.role !== 'superadmin') return;
    fetchTopups();
    fetchSettings();
  }, [user, fetchTopups, fetchSettings]);

  const handleApprove = async (topupId) => {
    const notes = window.prompt('Approval notes (optional):', '');
    if (notes === null) return;
    setProcessingId(topupId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/credit-topup-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topup_id: topupId, notes: notes || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to approve');
      setStatusMessage(data.message);
      await fetchTopups();
    } catch (err) {
      setError(err.message || 'Failed to approve top-up');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (topupId) => {
    const notes = window.prompt('Reason for rejection:', '');
    if (notes === null) return;
    setProcessingId(topupId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/credit-topup-reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topup_id: topupId, notes: notes || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to reject');
      setStatusMessage(data.message);
      await fetchTopups();
    } catch (err) {
      setError(err.message || 'Failed to reject top-up');
    } finally {
      setProcessingId(null);
    }
  };

  const saveSetting = async (field, value) => {
    setSavingField(field);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/credit-topup-settings-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update setting');
      await fetchSettings();
    } catch (err) {
      setError(err.message || 'Failed to update setting');
    } finally {
      setSavingField(null);
    }
  };

  const handleUploadQr = async (qrType, file) => {
    setUploadingQr(qrType);
    setError('');
    try {
      const form = new FormData();
      form.append('qr_type', qrType);
      form.append('qr_image', file);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/credit-topup-qr-upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Failed to upload QR code');
      await fetchSettings();
    } catch (err) {
      setError(err.message || 'Failed to upload QR code');
    } finally {
      setUploadingQr(null);
    }
  };

  if (!user || user.role !== 'superadmin') {
    return null;
  }

  const filtered = topups.filter((t) => t.status === activeTab);
  const counts = {
    pending_review: topups.filter((t) => t.status === 'pending_review').length,
    approved: topups.filter((t) => t.status === 'approved').length,
    rejected: topups.filter((t) => t.status === 'rejected').length,
    awaiting_payment: topups.filter((t) => t.status === 'awaiting_payment').length,
  };

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-headline-lg font-bold mb-6" style={{ color: '#c9a961' }}>
          Credit Points Top-Ups
        </h1>

        {error && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
            {error}
          </div>
        )}
        {statusMessage && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#b8a68f' }}>
            {statusMessage}
          </div>
        )}

        <div className="flex gap-2 mb-4 border-b flex-wrap" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
          {[
            ['pending_review', 'Pending Review'],
            ['approved', 'Approved'],
            ['rejected', 'Rejected'],
            ['awaiting_payment', 'Awaiting Payment'],
          ].map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => setActiveTab(val)}
              className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
              style={activeTab === val ? { color: '#0d2818', backgroundColor: '#c9a961' } : { color: '#c9a961', backgroundColor: 'transparent' }}
            >
              {label} ({counts[val]})
            </button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: '#b8a68f' }}>Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p style={{ color: '#b8a68f' }}>No {activeTab.replace('_', ' ')} top-ups.</p>
          </div>
        ) : (
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
              <thead>
                <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                  <th className="p-2">Member</th>
                  <th className="p-2">Method</th>
                  <th className="p-2">Amount (CP)</th>
                  <th className="p-2">Charged</th>
                  <th className="p-2">Proof</th>
                  <th className="p-2">Payment Ref</th>
                  <th className="p-2">Date</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                    <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(t.member)}</td>
                    <td className="p-2">{t.method}</td>
                    <td className="p-2">{Number(t.amount_cp).toFixed(2)}</td>
                    <td className="p-2">{t.amount_charged ? `${Number(t.amount_charged).toFixed(2)} ${t.currency || ''}` : '—'}</td>
                    <td className="p-2">
                      {t.proof_url ? (
                        <a href={t.proof_url} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#c9a961' }}>
                          View
                        </a>
                      ) : '—'}
                    </td>
                    <td className="p-2 font-mono text-body-xs" style={{ color: '#999999' }} title={t.stripe_checkout_session_id || ''}>
                      {t.stripe_payment_intent_id || t.stripe_checkout_session_id || '—'}
                    </td>
                    <td className="p-2">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="p-2">
                      {t.status === 'pending_review' && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleApprove(t.id)}
                            disabled={processingId === t.id}
                            className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReject(t.id)}
                            disabled={processingId === t.id}
                            className="px-3 py-1 rounded font-medium text-body-xs border disabled:opacity-50"
                            style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      {t.status !== 'pending_review' && t.admin_notes && (
                        <span className="text-body-xs" style={{ color: '#999999' }}>{t.admin_notes}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>Top-Up Settings</h3>
          <div className="space-y-3">
            <SettingsField label="USDT Wallet Address" value={settings?.usdt_wallet_address} onSave={(v) => saveSetting('usdt_wallet_address', v)} saving={savingField === 'usdt_wallet_address'} />
            <QrCodeField label="USDT QR Code" imageUrl={settings?.usdt_qr_url} onUpload={(f) => handleUploadQr('usdt', f)} uploading={uploadingQr === 'usdt'} />
            <QrCodeField label="PayNow QR Code" imageUrl={settings?.paynow_qr_url} onUpload={(f) => handleUploadQr('paynow', f)} uploading={uploadingQr === 'paynow'} />
            <SettingsField label="Bank Name" value={settings?.bank_name} onSave={(v) => saveSetting('bank_name', v)} saving={savingField === 'bank_name'} />
            <SettingsField label="Bank Account Number" value={settings?.bank_account_number} onSave={(v) => saveSetting('bank_account_number', v)} saving={savingField === 'bank_account_number'} />
            <SettingsField label="Bank Account Holder" value={settings?.bank_account_holder} onSave={(v) => saveSetting('bank_account_holder', v)} saving={savingField === 'bank_account_holder'} />
            <SettingsField label="Bank SWIFT Code" value={settings?.bank_swift_code} onSave={(v) => saveSetting('bank_swift_code', v)} saving={savingField === 'bank_swift_code'} />
            <SettingsField label="USDT Processing Fee (%)" value={settings?.usdt_fee_percent} onSave={(v) => saveSetting('usdt_fee_percent', v)} saving={savingField === 'usdt_fee_percent'} type="number" />
            <SettingsField label="Member Buy Spread (%)" value={settings?.member_buy_spread_percent} onSave={(v) => saveSetting('member_buy_spread_percent', v)} saving={savingField === 'member_buy_spread_percent'} type="number" />
          </div>
        </div>
      </div>
    </div>
  );
}
